import { z } from "zod";
import { MediaStatus } from "../../lib/enums";
import { MediaContactTagInputSchema, MediaContactTagSchema } from "../mediaContactTag";

/**
 * The pending-submission record `finalizeUpload` hands back once the
 * direct-to-B2 upload has been validated and recorded.
 */
export const GalleryPhotoUploadResponseSchema = z.object({
  id: z.string(),
  gallery_id: z.string(),
  status: z.nativeEnum(MediaStatus),
  filename: z.string(),
  mime_type: z.string(),
  byte_size: z.number(),
  width: z.number().nullable(),
  height: z.number().nullable(),
  created_at: z.string(),
  /**
   * Short-lived signed URL for the server-generated web derivative — always a
   * browser-displayable JPEG, so the upload wizard's review step can show
   * HEIC uploads the browser could never decode locally.
   */
  preview_url: z.string(),
});
export type GalleryPhotoUploadResponse = z.infer<typeof GalleryPhotoUploadResponseSchema>;

// ----- Presigned direct-to-B2 uploads -----

export const GalleryUploadRequestInputSchema = z.object({
  event_id: z.string(),
  filename: z.string().min(1).max(255),
  /** As the browser reports it — may be empty or octet-stream for HEIC. */
  mime_type: z.string().max(255),
  byte_size: z.number().int().positive(),
});
export type GalleryUploadRequestInput = z.infer<typeof GalleryUploadRequestInputSchema>;

export const GalleryUploadTicketSchema = z.object({
  media_id: z.string(),
  gallery_id: z.string(),
  /** Short-lived presigned PUT; the signature pins content type and size. */
  upload_url: z.string(),
  /** The canonical type the PUT must send — part of the signature. */
  content_type: z.string(),
});
export type GalleryUploadTicket = z.infer<typeof GalleryUploadTicketSchema>;

export const GalleryFinalizeUploadInputSchema = z.object({
  event_id: z.string(),
  /** Strict UUID: this id is interpolated into storage keys server-side. */
  media_id: z.string().uuid(),
  filename: z.string().min(1).max(255),
  mime_type: z.string().max(255),
  /** Optional caption written by the uploader in the queue row. */
  description: z.string().max(2000).nullish(),
});
export type GalleryFinalizeUploadInput = z.infer<typeof GalleryFinalizeUploadInputSchema>;

// ----- Post-upload review (the upload wizard's edit step) -----
// All three procedures below act on the caller's OWN, still-PENDING uploads:
// the wizard edits descriptions/tags between finalizing the files and
// concluding the submission.

/** Tag a contact on one of the caller's own pending uploads. */
export const GalleryAddOwnPhotoTagInputSchema = MediaContactTagInputSchema.extend({
  media_id: z.string(),
});
export type GalleryAddOwnPhotoTagInput = z.infer<typeof GalleryAddOwnPhotoTagInputSchema>;

/** Remove a tag from one of the caller's own pending uploads. */
export const GalleryRemoveOwnPhotoTagInputSchema = z.object({
  tag_id: z.string(),
});
export type GalleryRemoveOwnPhotoTagInput = z.infer<typeof GalleryRemoveOwnPhotoTagInputSchema>;

export const GalleryRemoveOwnPhotoTagOutputSchema = z.object({
  id: z.string(),
});
export type GalleryRemoveOwnPhotoTagOutput = z.infer<typeof GalleryRemoveOwnPhotoTagOutputSchema>;

/** Set or clear (null) the description on one of the caller's own pending uploads. */
export const GalleryUpdateOwnPhotoDescriptionInputSchema = z.object({
  media_id: z.string(),
  description: z.string().max(2000).nullable(),
});
export type GalleryUpdateOwnPhotoDescriptionInput = z.infer<
  typeof GalleryUpdateOwnPhotoDescriptionInputSchema
>;

export const GalleryUpdateOwnPhotoDescriptionOutputSchema = z.object({
  id: z.string(),
  description: z.string().nullable(),
});
export type GalleryUpdateOwnPhotoDescriptionOutput = z.infer<
  typeof GalleryUpdateOwnPhotoDescriptionOutputSchema
>;

/** Fired once per submission batch, after the upload queue drains. */
export const CompleteGallerySubmissionInputSchema = z.object({
  eventId: z.string(),
  uploadedCount: z.number().int().min(1),
});
export type CompleteGallerySubmissionInput = z.infer<typeof CompleteGallerySubmissionInputSchema>;

/**
 * One approved photo in an event's gallery. Gallery objects live in the private
 * bucket, so both URLs are short-lived signed links rather than stable
 * addresses — a photo with no usable object is left out of the list entirely,
 * which is why neither URL is nullable. `tags` carries CONFIRMED contact tags
 * only: pending tags are visible solely in the admin review queue, never to
 * gallery viewers.
 */
export const EventGalleryPhotoSchema = z.object({
  id: z.string(),
  thumbnail_url: z.string(),
  web_url: z.string(),
  width: z.number().nullable(),
  height: z.number().nullable(),
  description: z.string().nullable(),
  created_at: z.string(),
  tags: z.array(MediaContactTagSchema),
});
export type EventGalleryPhoto = z.infer<typeof EventGalleryPhotoSchema>;

/**
 * Who submitted a photo. `username` is null when the uploading contact has no
 * linked account (or the account is gone), in which case there is no profile
 * page to link to and the name renders as plain text.
 */
export const GalleryPhotoUploaderSchema = z.object({
  display_name: z.string(),
  username: z.string().nullable(),
});
export type GalleryPhotoUploader = z.infer<typeof GalleryPhotoUploaderSchema>;

/** A gallery-page photo: the event-gallery shape plus uploader attribution. */
export const GalleryDetailPhotoSchema = EventGalleryPhotoSchema.extend({
  uploader: GalleryPhotoUploaderSchema,
});
export type GalleryDetailPhoto = z.infer<typeof GalleryDetailPhotoSchema>;

/**
 * One gallery in the members' gallery index. Only galleries with at least one
 * approved photo are listed, so `photo_count` is always ≥ 1 and `cover_url`
 * always resolves — it is a short-lived signed link to the newest approved
 * photo's thumbnail (or its web derivative if no thumbnail was cut).
 * `event.start_date` is the ISO event date the index displays and sorts by;
 * `event` is null for galleries not created from an event.
 */
export const GalleryListItemSchema = z.object({
  id: z.string(),
  title: z.string(),
  description: z.string().nullable(),
  event: z
    .object({
      id: z.string(),
      name: z.string(),
      start_date: z.string().nullable(),
      end_date: z.string().nullable(),
    })
    .nullable(),
  photo_count: z.number().int(),
  cover_url: z.string(),
});
export type GalleryListItem = z.infer<typeof GalleryListItemSchema>;

export const GalleryListOutputSchema = z.array(GalleryListItemSchema);
export type GalleryListOutput = z.infer<typeof GalleryListOutputSchema>;

/** Photos per gallery page. */
export const GALLERY_PHOTOS_PER_PAGE = 50;

export const GalleryGetInputSchema = z.object({
  gallery_id: z.string(),
  page: z.number().int().min(1).default(1),
  per_page: z.number().int().min(1).max(100).default(GALLERY_PHOTOS_PER_PAGE),
});
export type GalleryGetInput = z.infer<typeof GalleryGetInputSchema>;

/**
 * One gallery with a page of its approved photos. `event` is null for
 * galleries that were not created from an event — `event_id` is nullable on
 * the table. `can_contribute` is the caller's eligibility to submit photos:
 * the same gate as the event page's, and always false for an event-less
 * gallery, since uploads are event-bound. `photos` holds only the requested
 * page; `total` counts every displayable approved photo, so a page past the
 * end comes back empty alongside the real total.
 */
export const GalleryDetailSchema = z.object({
  id: z.string(),
  title: z.string(),
  description: z.string().nullable(),
  event: z
    .object({
      id: z.string(),
      name: z.string(),
      start_date: z.string().nullable(),
      end_date: z.string().nullable(),
    })
    .nullable(),
  can_contribute: z.boolean(),
  photos: z.array(GalleryDetailPhotoSchema),
  page: z.number().int(),
  per_page: z.number().int(),
  total: z.number().int(),
});
export type GalleryDetail = z.infer<typeof GalleryDetailSchema>;

/**
 * Ensure an event's gallery exists so the member can be sent to its page.
 * Galleries normally appear on first upload; this covers the "no uploads yet"
 * event page, where Submit Photos needs a gallery to link to.
 */
export const EnsureGalleryForEventInputSchema = z.object({
  event_id: z.string(),
});
export type EnsureGalleryForEventInput = z.infer<typeof EnsureGalleryForEventInputSchema>;

export const EnsureGalleryForEventOutputSchema = z.object({
  gallery_id: z.string(),
});
export type EnsureGalleryForEventOutput = z.infer<typeof EnsureGalleryForEventOutputSchema>;

export const EventGalleryGetByEventInputSchema = z.object({
  event_id: z.string(),
});
export type EventGalleryGetByEventInput = z.infer<typeof EventGalleryGetByEventInputSchema>;

/**
 * `gallery_id` links the event page to the full gallery page. Null until the
 * first upload creates the event's gallery — an event nobody has submitted
 * photos for has no gallery row at all.
 */
export const EventGalleryGetByEventOutputSchema = z.object({
  gallery_id: z.string().nullable(),
  items: z.array(EventGalleryPhotoSchema),
});
export type EventGalleryGetByEventOutput = z.infer<typeof EventGalleryGetByEventOutputSchema>;
